/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { InfoCardKernel } from 'client-code/actions';

import { IInfoCard } from './IInfoCard';
import { IInfoCardEvents } from './IInfoCardEvents';
import { IInfoCardProperty } from './IInfoCardProperty';
import { IInfoCardUserInputEvent } from './IInfoCardUserInputEvent';
import { InfoCardUserInputHandler } from './InfoCardUserInputHandler';

export class InfoCardManager implements IInfoCard, IInfoCardEvents, IInfoCardUserInputEvent {
    private static mInstance: InfoCardManager;

    private mContextStore: Map<string, { [key: string]: any }> = new Map();

    private mInfoCardDismissedCallback: () => void;

    private mInfoCardKernel: InfoCardKernel | null;

    private mInfoCardKernelChangeListener: (infoCardKernel: InfoCardKernel | null) => void;

    private mInfoCardProperties: IInfoCardProperty;

    private mInfoCardPropertiesChangeListener: (properties: IInfoCardProperty) => void;

    private mInfoCardVisibility: boolean;

    private mInfoCardVisibilityChangeListener: (isVisible: boolean) => void;

    private mUserInputHandler: InfoCardUserInputHandler | null;

    private mScrollHandler: ((options?: ScrollToOptions) => void) | null;

    private readonly SCROLL_AMOUNT = 100;

    private constructor() {
        this.mInfoCardVisibility = false;
        this.mInfoCardKernel = null;
        this.mInfoCardProperties = {
            hasItemsBefore: false,
            hasItemsAfter: false,
            actionButtonVisibility: false,
            actionButtonCount: 0,
        };
        this.mInfoCardDismissedCallback = (): void => {};
        this.mInfoCardKernelChangeListener = (): void => {};
        this.mInfoCardVisibilityChangeListener = (): void => {};
        this.mInfoCardPropertiesChangeListener = (): void => {};
        this.mUserInputHandler = null;
        this.mScrollHandler = null;
    }

    /**
     * Notifies listeners of a change in the InfoCardKernel.
     * @param infoCardKernel - The new InfoCardKernel or null.
     */
    private notifyInfoCardKernelChange(infoCardKernel: InfoCardKernel | null): void {
        this.mInfoCardKernelChangeListener(infoCardKernel);
    }

    /**
     * Notifies listeners of a change in the InfoCard property.
     * @param property - The new property state.
     */
    private notifyInfoCardPropertyChange(property: IInfoCardProperty): void {
        this.mInfoCardPropertiesChangeListener(property);
    }

    /**
     * Notifies listeners of a change in the InfoCard visibility.
     * @param isVisible - The new visibility state (true if visible).
     */
    private notifyInfoCardVisibilityChange(isVisible: boolean): void {
        this.mInfoCardVisibilityChangeListener(isVisible);
    }

    /**
     * @inheritdoc
     */
    public addUserInputEventHandler(handler: InfoCardUserInputHandler): void {
        this.mUserInputHandler = handler;
    }

    /**
     * @inheritdoc
     */
    public clearContext(context: string): void {
        this.mContextStore.delete(context);
    }

    /**
     * @inheritdoc
     */
    public getContextData(context: string): { [key: string]: any } | undefined {
        return this.mContextStore.get(context);
    }

    /**
     * @inheritdoc
     */
    public getInfoCardKernel(): InfoCardKernel | null {
        return this.mInfoCardKernel;
    }

    /**
     * @inheritdoc
     */
    public getProperties(): IInfoCardProperty {
        return this.mInfoCardProperties;
    }

    /**
     * @inheritdoc
     */
    public getProperty<K extends keyof IInfoCardProperty>(key: K): IInfoCardProperty[K] {
        return this.mInfoCardProperties[key];
    }

    /**
     * @inheritdoc
     */
    public dismiss(): void {
        this.hide();
        this.mInfoCardKernel = null;
        this.notifyInfoCardKernelChange(this.mInfoCardKernel);
        this.mInfoCardDismissedCallback();
        this.mInfoCardProperties = {
            hasItemsBefore: false,
            hasItemsAfter: false,
            actionButtonVisibility: false,
            actionButtonCount: 0,
        };
        this.notifyInfoCardPropertyChange(this.mInfoCardProperties);
    }

    /**
     * @inheritdoc
     */
    public hide(): void {
        this.mInfoCardVisibility = false;
        this.notifyInfoCardVisibilityChange(this.mInfoCardVisibility);
    }

    /**
     * This method checks the visibility state of the InfoCard action buttons. If the buttons
     * are visible, it updates the visibility state to `false` and notifies the registered
     * listener about the change in visibility.
     * Ensures no redundant notifications are sent when already hidden.
     */
    public hideActionButtons(): void {
        if (this.mInfoCardProperties.actionButtonVisibility) {
            this.mInfoCardProperties.actionButtonVisibility = false;
            this.notifyInfoCardPropertyChange(this.mInfoCardProperties);
        }
    }

    /**
     * @inheritdoc
     */
    public onInfoCardDismissed(callback: () => void): void {
        this.mInfoCardDismissedCallback = callback;
    }

    /**
     * @inheritdoc
     */
    public onInfoCardKernelChanged(
        callback: (infoCardKernel: InfoCardKernel | null) => void,
    ): void {
        this.mInfoCardKernelChangeListener = callback;
    }

    /**
     * @inheritdoc
     */
    public onInfoCardPropertyChanged(callback: (properties: IInfoCardProperty) => void): void {
        this.mInfoCardPropertiesChangeListener = callback;
    }

    /**
     * @inheritdoc
     */
    public onInfoCardVisibilityChanged(callback: (isVisible: boolean) => void): void {
        this.mInfoCardVisibilityChangeListener = callback;
    }

    /**
     * @inheritdoc
     */
    public onKeyBack(): void {
        if (this.mUserInputHandler) {
            this.mUserInputHandler.onKeyBack(this.mInfoCardProperties);
        }
    }

    /**
     * @inheritdoc
     */
    public onKeyLeft(): void {
        if (this.mUserInputHandler) {
            this.mUserInputHandler.onKeyLeft(this.mInfoCardProperties);
        }
    }

    /**
     * @inheritdoc
     */
    public onKeyRight(): void {
        if (this.mUserInputHandler) {
            this.mUserInputHandler.onKeyRight(this.mInfoCardProperties);
        }
    }

    /**
     * @inheritdoc
     */
    public setInfoCardScrollHandler(handler: ((options?: ScrollToOptions) => void) | null): void {
        this.mScrollHandler = handler;
    }

    /**
     * @inheritdoc
     */
    public scroll(direction: 'up' | 'down'): void {
        if (this.mScrollHandler) {
            this.mScrollHandler({
                top: direction === 'up' ? -this.SCROLL_AMOUNT : this.SCROLL_AMOUNT,
                behavior: 'smooth',
            });
        }
    }

    /**
     * @inheritdoc
     */
    public setContextData(context: string, data: { [key: string]: any }): void {
        this.mContextStore.set(context, data);
    }

    /**
     * @inheritdoc
     */
    public setInfoCardKernel(infoCardKernel: InfoCardKernel): void {
        this.mInfoCardKernel = infoCardKernel;
        this.notifyInfoCardKernelChange(infoCardKernel);
    }

    /**
     * @inheritdoc
     */
    public setProperties(properties: Partial<IInfoCardProperty>): void {
        this.mInfoCardProperties = {
            ...this.mInfoCardProperties,
            ...properties,
        };
        this.notifyInfoCardPropertyChange(this.mInfoCardProperties);
    }

    /**
     * @inheritdoc
     */
    public setProperty<K extends keyof IInfoCardProperty>(
        key: K,
        value: IInfoCardProperty[K],
    ): void {
        this.mInfoCardProperties[key] = value;
        this.notifyInfoCardPropertyChange(this.mInfoCardProperties);
    }

    /**
     * @inheritdoc
     */
    public show(): void {
        this.mInfoCardVisibility = true;
        this.notifyInfoCardVisibilityChange(this.mInfoCardVisibility);
    }

    /**
     * @inheritdoc
     */
    public showActionButtons(): void {
        if (!this.mInfoCardProperties.actionButtonVisibility) {
            this.mInfoCardProperties.actionButtonVisibility = true;
            this.notifyInfoCardPropertyChange(this.mInfoCardProperties);
        }
    }

    /**
     * @inheritdoc
     */
    public isInfoCardVisible(): boolean {
        return this.mInfoCardVisibility;
    }

    /**
     * Gets the singleton instance of the InfoCardManager.
     * @returns The singleton instance of the InfoCardManager.
     */
    public static getInstance(): IInfoCard & IInfoCardEvents & IInfoCardUserInputEvent {
        if (!InfoCardManager.mInstance) {
            InfoCardManager.mInstance = new InfoCardManager();
        }
        return InfoCardManager.mInstance;
    }
}
